import type { ImporterOutputFieldType, SheetDefinition } from 'hello-csv/react';

// Creates a validator function that checks if email domain is allowed
const createDomainValidator = (allowedDomains: string[]) => {
  return (value: ImporterOutputFieldType): string | undefined => {
    if (!value || typeof value !== 'string') return undefined;

    const email = value.trim().toLowerCase();
    if (!email.includes('@')) return undefined; // Let the email validator handle format

    const domain = email.split('@')[1];
    if (!domain) return undefined;

    // If no domains are configured, allow all (backend will validate)
    if (allowedDomains.length === 0) return undefined;

    const isAllowed = allowedDomains.some(
      (allowedDomain) => domain === allowedDomain.toLowerCase(),
    );

    if (!isAllowed) {
      return 'Email address not available for this organization; contact support if you need further assistance';
    }

    return undefined;
  };
};

const createParticipantSheetDefinition = (allowedEmailDomains: string[]): SheetDefinition => ({
  id: 'participants',
  label: 'Confirm participants',
  columns: [
    {
      id: 'email',
      label: 'Email Address',
      type: 'string',
      suggestedMappingKeywords: ['email', 'email address', 'e-mail', 'mail'],
      validators: [
        { validate: 'required', error: 'Email address is required' },
        { validate: 'email', error: 'Email address has invalid formatting' },
        {
          validate: 'custom',
          key: 'domain-check',
          validateFn: createDomainValidator(allowedEmailDomains),
        },
      ],
    },
    {
      id: 'firstname',
      label: 'First Name',
      type: 'string',
      suggestedMappingKeywords: ['first name', 'firstname', 'first', 'given name'],
      validators: [{ validate: 'required', error: 'First name is required' }],
    },
    {
      id: 'lastname',
      label: 'Last Name',
      type: 'string',
      suggestedMappingKeywords: ['last name', 'lastname', 'last', 'surname', 'family name'],
      validators: [{ validate: 'required', error: 'Last name is required' }],
    },
    {
      id: 'login_type',
      label: 'Login Type',
      type: 'enum',
      suggestedMappingKeywords: ['login type', 'sso', 'authentication', 'login'],
      typeArguments: {
        values: [
          { value: '', label: 'Use Organization Setting' },
          { value: 'always_use_sso', label: 'SSO' },
          { value: 'do_not_use_sso', label: 'Never Use SSO' },
        ],
      },
    },
  ],
});

export const createHelloCsvSheets = (allowedEmailDomains: string[]): SheetDefinition[] => [
  createParticipantSheetDefinition(allowedEmailDomains),
];
